import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

const api = axios.create({
  baseURL: API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

export interface UserStatistics {
  totalProjects: number
  activeProjects: number
  completedProjects: number
  onHoldProjects: number
  totalRequirements: number
  totalUserStories: number
  totalTestCases: number
  totalTasks: number
  dailyUsed: number
  dailyLimit: number | null
  totalAllowance: number
  remainingTokens: number
  tokenLimitLabel: string
  modelUsage: Array<{ model: string, used: number }>
}

export interface UserWithStats {
  id: string
  username: string
  email: string
  firstName: string
  lastName: string
  companyName: string
  contactNumber: string
  emailVerified: boolean
  isActive: boolean
  subscribed: 'F' | 'P' | 'S'
  planType: 'free' | 'starter' | 'intelligence'
  role: 'user' | 'admin'
  createdAt: string
  statistics: UserStatistics
}

export interface DashboardStats {
  users: {
    total: number
    active: number
    admins: number
    subscribed: number
    free: number
  }
  projects: {
    total: number
    active: number
    completed: number
  }
  content: {
    requirements: number
    userStories: number
    testCases: number
    tasks: number
  }
}

export interface TokenUsageData {
  id: number
  userId: string
  userName: string
  modelUsed: string
  tokenCount: number
  documentType: string | null
  projectId: string | null
  projectName: string | null
  remainingTokens: number
  modelRemaining: number
  createdAt: string
}

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  message?: string
}

export const adminService = {
  async getAllUsers(): Promise<ApiResponse<UserWithStats[]>> {
    const response = await api.get('/admin/users')
    return response.data
  },

  async getUserById(userId: string): Promise<ApiResponse<{
    user: UserWithStats
    projects: any[]
    statistics: UserStatistics
  }>> {
    const response = await api.get(`/admin/users/${userId}`)
    return response.data
  },

  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    const response = await api.get('/admin/dashboard-stats')
    return response.data
  },

  async getTokenUsageData(page: number = 1, limit: number = 50, userId?: string): Promise<ApiResponse<{
    meta: any
    data: TokenUsageData[]
  }>> {
    const params: any = { page, limit }
    if (userId) {
      params.userId = userId
    }
    const response = await api.get('/admin/token-usage', { params })
    return response.data
  },

  async updateRole(userId: string, role: 'admin' | 'user'): Promise<ApiResponse> {
    const response = await api.patch(`/admin/users/${userId}/update-role`, { role })
    return response.data
  },

  async toggleStatus(userId: string): Promise<ApiResponse> {
    const response = await api.patch(`/admin/users/${userId}/toggle-status`)
    return response.data
  },

  async updatePlan(userId: string, plan: string): Promise<ApiResponse> {
    const response = await api.patch(`/admin/users/${userId}/update-plan`, { plan })
    return response.data
  },
  
  async getCoupons(): Promise<ApiResponse> {
    const response = await api.get('/admin/coupons')
    return response.data
  },

  async createCoupon(data: any): Promise<ApiResponse> {
    const response = await api.post('/admin/coupons', data)
    return response.data
  },

  async updateCoupon(id: number, data: any): Promise<ApiResponse> {
    const response = await api.put(`/admin/coupons/${id}`, data)
    return response.data
  },

  async deleteCoupon(id: number): Promise<ApiResponse> {
    const response = await api.delete(`/admin/coupons/${id}`)
    return response.data
  },

  async getModelRouting(): Promise<ApiResponse<any[]>> {
    const response = await api.get('/admin/model-routing')
    return response.data
  },

  async updateModelRouting(id: number, data: { model_id: string; fallback_models?: string; score?: number }): Promise<ApiResponse<any>> {
    const response = await api.put(`/admin/model-routing/${id}`, data)
    return response.data
  },
}

export default adminService
